import { CollapseSection, Modal } from "@msys/ui";
import { Grid, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { uniqueId } from "lodash-es";
import moment from "moment";
import React, { useState } from "react";
import * as Yup from "yup";
import { OrganisationRole as GQLOrganisationRole } from "../../../clients/graphqlTypes.js";
import { CheckboxField } from "../../commons/form-fields/CheckboxField.js";
import { DateTimePickerField } from "../../commons/form-fields/DateTimePickerField.js";
import { SwitchField } from "../../commons/form-fields/SwitchField.js";
import { TextField } from "../../commons/form-fields/TextField.js";
import { AddressDetails__AddressFragment } from "../addresses/Addresses.generated.js";
import { getAddressLabel } from "../addresses/helpers.js";
import { OrganisationMembersField } from "../organisations/form-fields/OrganisationMembersField.js";
import { useOrganisationRoles } from "../users/useRoles.js";

const FILTERS = { statuses: ["ACTIVATED" as const] };

type FormValues = {
  onTop: boolean;
  maxDistance: number;
  resourceList: string[];
  organisationRoleIds: { [id: string]: boolean };
  availability: {
    from: moment.Moment | null;
    till: moment.Moment | null;
  };
};

export const ResourceFilterModal = ({
  title,
  usePinnedResources = true,
  useAvailability = true,
  useMaxDistance = false,
  projectAddress,
  roles,
  resourceList,
  resourceMaxDistance,
  resourceOnTop,
  resourceOrganisationRoleIds,
  resourceAvailability,
  handleClose,
  handleComplete,
}: {
  title?: string;
  usePinnedResources?: boolean;
  useAvailability?: boolean;
  useMaxDistance?: boolean;
  projectAddress?: AddressDetails__AddressFragment;
  roles: GQLOrganisationRole[];
  resourceList: string[] | null | undefined;
  resourceMaxDistance: number | undefined;
  resourceOnTop: boolean | undefined;
  resourceOrganisationRoleIds: string[] | null | undefined;
  resourceAvailability:
    | [moment.Moment | null, moment.Moment | null]
    | null
    | undefined;
  handleClose: () => void;
  handleComplete: (values: {
    resourceList: string[] | null;
    resourceMaxDistance: number;
    resourceOrganisationRoleIds: string[] | null;
    resourceAvailability: [moment.Moment | null, moment.Moment | null] | null;
    resourceOnTop: boolean;
  }) => void;
}) => {
  const { t } = useTranslate(["PlanningModal", "Global"]);

  const formId = React.useMemo(() => uniqueId(), []);

  const { getOrganisationRoleOptions } = useOrganisationRoles();

  const roleOptions = getOrganisationRoleOptions(roles);

  const handleSubmit = async (values: FormValues) => {
    const selectedOrganisationRoleIds = Object.keys(
      values.organisationRoleIds
    ).filter(roleId => values.organisationRoleIds?.[roleId]) as string[];

    handleComplete({
      resourceMaxDistance: values.maxDistance,
      resourceAvailability:
        values.availability.from || values.availability.till
          ? [values.availability.from ?? null, values.availability.till ?? null]
          : null,
      resourceOrganisationRoleIds:
        selectedOrganisationRoleIds.length === roleOptions.length
          ? null
          : selectedOrganisationRoleIds,
      resourceList:
        values.resourceList.length === 0 ? null : values.resourceList,
      resourceOnTop: values.onTop,
    });
    handleClose();
  };

  const initialValues: FormValues = {
    onTop: resourceOnTop ?? true,
    maxDistance: resourceMaxDistance ?? 0,
    resourceList: resourceList || [],
    organisationRoleIds: !resourceOrganisationRoleIds
      ? roleOptions.reduce(
          (acc, role) => ({
            ...acc,
            [role.value]: true,
          }),
          {}
        )
      : resourceOrganisationRoleIds.reduce(
          (acc, roleId) => ({
            ...acc,
            [roleId]: true,
          }),
          {}
        ),
    availability: resourceAvailability
      ? {
          from: resourceAvailability[0] ?? null,
          till: resourceAvailability[1] ?? null,
        }
      : { from: null, till: null },
  };

  const validationSchema = Yup.object().shape({
    onTop: Yup.boolean(),
    maxDistance: Yup.number()
      .label(
        t("Members within radius", {
          ns: "PlanningModal",
        })
      )
      .min(0),
    availability: Yup.object().shape({
      from: Yup.date()
        .nullable()
        .label(
          t("From", {
            ns: "PlanningModal",
          })
        ),
      till: Yup.date()
        .nullable()
        .label(
          t("Until", {
            ns: "PlanningModal",
          })
        )
        .test(
          "laterThan",
          t("Until must be later than from", {
            ns: "PlanningModal",
          }),
          function (till: Date | null | undefined) {
            const from: Date | null | undefined = this.resolve(Yup.ref("from"));
            return !till || !from || from < till;
          }
        ),
    }),
  });

  const [resourceSearch, setResourceSearch] = useState("");

  return (
    <Formik<FormValues>
      validateOnMount
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, isValid }) => {
        const selectedRoleIds = Object.keys(values.organisationRoleIds).filter(
          roleId => values.organisationRoleIds?.[roleId]
        );
        return (
          <Modal
            title={
              title ??
              t("Filter resources", {
                ns: "PlanningModal",
              })
            }
            actionButtons={[
              {
                label: t("Cancel", {
                  ns: "Global",
                }),
                handleClick: handleClose,
                buttonProps: { variant: "text" },
              },
              {
                label: t("Confirm", {
                  ns: "Global",
                }),
                buttonProps: {
                  type: "submit",
                  form: formId,
                  disabled: !isValid,
                },
              },
            ]}
            handleClose={handleClose}
          >
            <Form id={formId}>
              <Stack direction="column" spacing={1}>
                {usePinnedResources && (
                  <SwitchField
                    name="onTop"
                    label={t("Planned team members at the top", {
                      ns: "PlanningModal",
                    })}
                  />
                )}

                {/* Availability */}
                {useAvailability && (
                  <CollapseSection
                    title={t("Available full-time", {
                      ns: "PlanningModal",
                    })}
                    isInitiallyExpanded={Boolean(
                      values.availability.from || values.availability.till
                    )}
                  >
                    <Grid container spacing={1}>
                      <Grid size={6}>
                        <DateTimePickerField
                          label={t("From", {
                            ns: "PlanningModal",
                          })}
                          name={`availability.from`}
                          disabled={false}
                          isClearable={true}
                        />
                      </Grid>
                      <Grid size={6}>
                        <DateTimePickerField
                          label={t("Until", {
                            ns: "PlanningModal",
                          })}
                          name={`availability.till`}
                          disabled={false}
                          isClearable={true}
                        />
                      </Grid>
                    </Grid>
                  </CollapseSection>
                )}

                {/* Max distance */}
                {useMaxDistance && Boolean(projectAddress) && (
                  <CollapseSection
                    title={t("Available within distance", {
                      ns: "PlanningModal",
                    })}
                    isInitiallyExpanded={values.maxDistance > 0}
                  >
                    <TextField
                      label={t("Members within radius", {
                        ns: "PlanningModal",
                      })}
                      name="maxDistance"
                      type="number"
                      slotProps={{
                        htmlInput: {
                          min: 0,
                          step: 0.25,
                        },
                      }}
                      helperText={t("in km; 0 = any; from {address}", {
                        ns: "PlanningModal",
                        address: getAddressLabel(projectAddress) ?? "",
                      })}
                    />
                  </CollapseSection>
                )}

                {/* Organisation roles */}
                <CollapseSection
                  title={
                    t("Organisation roles", {
                      ns: "PlanningModal",
                    }) +
                    ": " +
                    t("{selectedCount}/{totalCount} selected", {
                      ns: "Global",
                      selectedCount: selectedRoleIds.length,
                      totalCount: roleOptions.length,
                    })
                  }
                >
                  {roleOptions.map(role => (
                    <CheckboxField
                      key={role.value}
                      name={`organisationRoleIds[${role.value}]`}
                      label={role.label}
                    />
                  ))}
                </CollapseSection>

                {/* Team members */}
                <CollapseSection
                  title={
                    t("Team members", {
                      ns: "PlanningModal",
                    }) +
                    ": " +
                    t("{selectedCount} selected", {
                      ns: "Global",
                      selectedCount: values.resourceList.length,
                    })
                  }
                >
                  <Stack direction="column" spacing={0}>
                    <OrganisationMembersField
                      name="resourceList"
                      inputLabel={t("Team members", { ns: "PlanningModal" })}
                      filters={FILTERS}
                    />
                  </Stack>
                </CollapseSection>
              </Stack>
            </Form>
          </Modal>
        );
      }}
    </Formik>
  );
};
